import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SponsorQuoteDialog } from "./sponsor-quote-dialog";
import { track } from "@/lib/telemetry";
import { safeHref } from "@/lib/safe-url";
import type { SponsorAdPublic } from "@dugads/shared";

interface SponsorAdCardProps {
  ad: SponsorAdPublic;
  schoolId?: string;
}

// 090: when cta_url is set, the carousel renders a plain link button that
// either navigates internally (starts with /) or opens externally. If it's
// unset we fall back to the Get-a-Quote dialog flow (067).
function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function SponsorAdCard({ ad, schoolId }: SponsorAdCardProps) {
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const hasCtaUrl = !!ad.cta_url;

  function handleCtaClick() {
    // 090 click telemetry on the Buy-style link ads
    track({
      type: "action",
      page: "sponsor_ad",
      action: "sponsor_ad_cta_click",
      value: ad.config_id,
    });
  }

  return (
    <>
      <Card className="border-dashed">
        <CardContent className="flex items-center gap-4 py-3 px-4">
          {ad.logo_url && (
            <img src={ad.logo_url} alt={ad.provider_name} className="h-10 w-10 object-contain rounded" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{ad.provider_name}</p>
            {ad.tagline && <p className="text-xs text-muted-foreground truncate">{ad.tagline}</p>}
          </div>
          {hasCtaUrl ? (
            isExternalUrl(ad.cta_url!) ? (
              <a
                href={safeHref(ad.cta_url!)}
                target="_blank"
                rel="noopener noreferrer"
                className={buttonVariants({ size: "sm", variant: "outline" })}
                onClick={handleCtaClick}
                data-action="sponsor-ad-cta"
              >
                {ad.cta_label}
              </a>
            ) : (
              <Link
                to={ad.cta_url!}
                className={buttonVariants({ size: "sm", variant: "outline" })}
                onClick={handleCtaClick}
                data-action="sponsor-ad-cta"
              >
                {ad.cta_label}
              </Link>
            )
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowQuoteDialog(true)}>
              {ad.cta_label}
            </Button>
          )}
        </CardContent>
      </Card>
      {showQuoteDialog && (
        <SponsorQuoteDialog ad={ad} schoolId={schoolId} onClose={() => setShowQuoteDialog(false)} />
      )}
    </>
  );
}
